import { InfoIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from 'components/button/Button';
import { FormikProvider } from 'formik';
import { useFormik } from 'components/form/formik';
import { createOrderLinePurchase } from '../../models';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { nullthrows } from '@utils/invariant';
import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';

import { ISimpleComboboxItem } from '../../../../components/combobox/SimpleCombobox';
import { InputField } from '../../../../components/input/InputField';
import {
  calculateDieselSurchargeFromLine,
  findDieselSurchargeBaseLine,
  getEffectiveDieselSurchargePercentage,
  isDieselSurchargeProductType,
} from '../../../../domain/diesel-surcharge';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { ProductTypeComboboxField, productTypeToComboboxItem } from '../../productType/ProductTypeComboboxField';
import { useOrderLine } from '../orderLine/orderLineContext';
import { useOrder } from '../order/orderContext';
import { MinimalGeneralSettings, useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import { IMinimalVatRate, VatRateComboboxField, vatRateToComboboxItem } from '../../vatRate/VatRateComboboxField';
import { useAuth } from '../../../../contexts/auth-context';
import { roleToNumber } from '../../../auth/roles.client';
import { Alert } from '../../../../components/alert/Alert';
import { captureException } from '@sentry/react';

export const createOrderPurchaseSchema = Yup.object().shape({
  productType: Yup.mixed().nullable().required('Vereist'),
  vatRate: Yup.mixed().nullable().required('Vereist'),
  amount: Yup.number().required('Vereist'),
  unitPrice: Yup.number().required('Vereist'),
  externalNote: Yup.string().nullable(),
});

export interface IOrderPurchaseValues {
  productType: ISimpleComboboxItem | null;
  vatRate: ISimpleComboboxItem | null;
  amount: string;
  unitPrice: string;
  externalNote: string;
}

export const getInitialValues = (
  settings: MinimalGeneralSettings,
  purchaseCount: number,
  supplier?: { defaultVatRate?: IMinimalVatRate | null } | null,
): IOrderPurchaseValues => {
  const defaultVatRate = supplier?.defaultVatRate ?? settings.defaultVatRate;
  const defaultProductType =
    purchaseCount > 0
      ? purchaseCount > 1
        ? settings.thirdDefaultProductType
        : settings.secondDefaultProductType
      : settings.defaultProductType;
  return {
    productType: defaultProductType ? productTypeToComboboxItem(defaultProductType) : null,
    vatRate: defaultVatRate ? vatRateToComboboxItem(defaultVatRate) : null,
    amount: '1',
    unitPrice: '0',
    externalNote: '',
  };
};

export const CreateOrderPurchasePage = () => {
  const { orderLine, refreshData: refetchOrderLine } = useOrderLine();
  const navigate = useNavigate();
  const { order } = useOrder();
  const { me } = useAuth();
  const isPurchaseReadOnly = order.purchaseLocked && roleToNumber(me.role) < roleToNumber('InternalAdmin');
  const { settings } = useMinimalGeneralSettings();

  const purchases = orderLine.purchases;
  const purchaseCount = purchases.length;
  const initialValues: IOrderPurchaseValues = useMemo(
    () => getInitialValues(settings, purchaseCount, order.supplier),
    [settings, purchaseCount, order.supplier],
  );

  const formikbag = useFormik({
    initialValues,
    validationSchema: createOrderPurchaseSchema,
    onSubmit: async (values) => {
      try {
        const { productType, vatRate, amount, unitPrice, externalNote, ...otherValues } = values;

        const productTypeId = nullthrows(productType?.key, 'Product type niet geselecteerd');
        const vatRateId = nullthrows(vatRate?.key, 'Btw type niet geselecteerd');
        await createOrderLinePurchase(orderLine.id, {
          productTypeId,
          vatRateId,
          amount: parseNumberInput(amount, 2),
          unitPrice: parseNumberInput(unitPrice, 2),
          externalNote: externalNote ?? '',
          ...otherValues,
        });
        refetchOrderLine();
        navigate('..');
        toast.success('Aankoop aangemaakt');
      } catch (err: any) {
        captureException(err);
        toast.error('Kon aankoop niet aanmaken: ' + getDisplayError(err));
      }
    },
  });
  const { handleSubmit, isSubmitting, values, setFieldValue } = formikbag;

  const dieselSurchargePercentage = getEffectiveDieselSurchargePercentage(
    order.supplier,
    settings.dieselSurchargePercentage,
  );
  const isDieselSurchargeSelected = isDieselSurchargeProductType(
    values.productType?.key,
    settings.dieselSurchargeProductType?.id,
  );
  const basePurchase = useMemo(
    () =>
      findDieselSurchargeBaseLine(
        purchases.map((purchase) => ({
          productTypeId: purchase.productType.id,
          amount: purchase.amount,
          unitPrice: purchase.unitPrice,
        })),
        {
          baseProductTypeId: settings.dieselSurchargeBaseProductType?.id,
          defaultProductTypeId: settings.defaultProductType?.id,
          dieselProductTypeId: settings.dieselSurchargeProductType?.id,
        },
      ),
    [
      purchases,
      settings.defaultProductType?.id,
      settings.dieselSurchargeBaseProductType?.id,
      settings.dieselSurchargeProductType?.id,
    ],
  );

  useEffect(() => {
    if (!isDieselSurchargeSelected || !basePurchase) {
      return;
    }
    const surchargeValue = calculateDieselSurchargeFromLine(basePurchase, dieselSurchargePercentage);
    setFieldValue('amount', '1');
    setFieldValue('unitPrice', decimateAndFormatNumber(surchargeValue, 2));
  }, [
    basePurchase?.amount,
    basePurchase?.productTypeId,
    basePurchase?.unitPrice,
    dieselSurchargePercentage,
    isDieselSurchargeSelected,
    setFieldValue,
    values.productType?.key,
  ]);

  if (isPurchaseReadOnly) {
    return (
      <Alert type="info" icon={<InfoIcon className="w-5 h-5" />} title="Aankoopzijde geblokkeerd">
        Het ritoverzicht of de self billing van deze order is al verzonden, dus er kunnen geen aankopen meer worden
        toegevoegd.
      </Alert>
    );
  }

  return (
    <>
      <h1 className="heading-one mb-4">Voeg aankoop toe</h1>

      <FormikProvider value={formikbag}>
        <form onSubmit={handleSubmit}>
          <ProductTypeComboboxField labelText="Product" name="productType" />

          {isDieselSurchargeSelected && (
            <div className="mb-4 text-sm">
              {`Diesel toeslag voor vervoerder is ${decimateAndFormatNumber(dieselSurchargePercentage, 2)}%`}
              {!basePurchase && ' — voeg eerst een transportaankoop toe om het bedrag te berekenen.'}
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-2">
              <InputField labelText="Aantal" type="number" name="amount" step="0.01" />
            </div>
            <div className="flex-2">
              <InputField labelText="Eenheidsprijs" type="number" name="unitPrice" step="0.01" />
            </div>
            <div className="flex-3">
              <VatRateComboboxField labelText="Btw Tarief" name="vatRate" />
            </div>
          </div>

          <InputField labelText="Extra info" type="text" name="externalNote" spellCheck={true} />

          <Button
            type="submit"
            color="primary"
            iconLeft={<PlusIcon className="button-icon" />}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
          >
            Voeg aankoop toe
          </Button>
        </form>
      </FormikProvider>
    </>
  );
};
